/**
 * Tipos centrais do motor do Tigrinho Tour (regras do Business Tour).
 *
 * O motor é uma função pura: (estado, ação, tempo) → (novo estado, eventos).
 * Nada aqui sabe de tela, rede ou som; isso tudo vive no cliente e no servidor.
 *
 * Unidade de dinheiro: milhares (1 = 1 mil contos na tela). Dinheiro inicial 2.000 = 2 milhões.
 */

export type PlayerId = string;

export type Mode = 'relampago' | 'padrao' | 'classico';

export type SquareKind = 'start' | 'city' | 'resort' | 'chance' | 'tax' | 'island' | 'championship' | 'worldTour';

export interface Square {
  index: number;
  kind: SquareKind;
  /** Grupo de cor (1 a 8), só para cidades. */
  group?: number;
  /** Lado do tabuleiro (0 a 3), para o monopólio de linha. Cantos não têm lado. */
  side?: number;
  /** Preço do terreno (bandeirola) ou do resort. */
  price?: number;
}

/**
 * Níveis de construção de uma cidade:
 * 0 = bandeirola (só o terreno), 1 = casa 1, 2 = casa 2, 3 = casa 3, 4 = hotel.
 */
export type Level = 0 | 1 | 2 | 3 | 4;

/** Cartas que ficam na mão do jogador até serem usadas. */
export type HeldCard = 'advogado' | 'seguro' | 'blackFriday' | 'cupom' | 'calote' | 'fazendeiro' | 'contador';

export interface Roll {
  dice: number[];
  total: number;
  doubles: boolean;
}

export interface Ownership {
  owner: PlayerId;
  /** Nível de construção (resorts ficam sempre em 0). */
  level: Level;
}

export interface PlayerStats {
  rentPaid: number;
  rentReceived: number;
  biggestRentPaid: number;
  attacksMade: number;
  attacksReceived: number;
  takeoversMade: number;
  takeoversSuffered: number;
  cardsDrawn: string[];
}

export interface Player {
  id: PlayerId;
  name: string;
  seat: number;
  cash: number;
  position: number;
  /** Preso na Ilha. */
  inJail: boolean;
  /** Preso por sonegação: não sai com dupla e a fiança é tripla. */
  evader?: boolean;
  jailAttempts: number;
  held: HeldCard[];
  /** Cartas compradas na lojinha (não fazem parte do baralho; somem quando usadas). */
  shop?: HeldCard[];
  laps: number;
  /** Caiu na Volta ao Mundo: na próxima vez pode voar para onde quiser. */
  worldTour: boolean;
  /** Quebrou e saiu da partida. */
  eliminated: boolean;
  stats: PlayerStats;
}

/** Efeitos temporários. `untilTurn`: vale enquanto turnCounter < untilTurn. */
export type Effect =
  | { kind: 'interdicted'; square: number; untilTurn: number }
  | { kind: 'viralizou'; player: PlayerId; untilTurn: number }
  | { kind: 'apagao'; untilTurn: number }
  | { kind: 'inflacao'; untilTurn: number }
  | { kind: 'busao'; player: PlayerId };

export type Creditor = PlayerId | 'bank';

export interface Debt {
  debtor: PlayerId;
  creditor: Creditor;
  amount: number;
  reason: string;
}

/** Passos de resolução que ainda vão acontecer neste turno, em ordem. */
export type Step =
  | { k: 'land'; player: PlayerId }
  | { k: 'magnet'; player: PlayerId }
  /** Invasão do MST numa cidade parada (player = dono). */
  | { k: 'mst'; player: PlayerId; square: number }
  | { k: 'passiveLand'; player: PlayerId }
  | { k: 'draw'; player: PlayerId }
  | { k: 'offerTakeover'; player: PlayerId; square: number }
  | { k: 'offerBuild'; player: PlayerId; square: number };

export interface Choice {
  id: string;
  player?: PlayerId;
  square?: number;
}

/** Decisão que trava a partida até alguém responder. */
export type Prompt =
  /** Comprar ou construir numa cidade/resort: escolhe até qual nível (de `from + 1` até `max`). */
  | { kind: 'build'; player: PlayerId; square: number; from: number; max: Level }
  /** Comprar a cidade de outro jogador pelo dobro do valor. */
  | { kind: 'takeover'; player: PlayerId; square: number; price: number }
  /** Escolher a cidade que sedia o Campeonato Mundial. */
  | { kind: 'championship'; player: PlayerId; options: number[] }
  /** Volta ao Mundo: escolher para onde voar. */
  | { kind: 'tour'; player: PlayerId; options: number[] }
  /** MST invadiu: chamar o fazendeiro (pagando) ou perder um nível. */
  | { kind: 'mst'; player: PlayerId; square: number; cost: number }
  /** "Faz o L": pagar o imposto ou tentar sonegar. */
  | { kind: 'tax'; player: PlayerId; square: number; amount: number; evaded: number; caught: number }
  | {
      kind: 'card';
      player: PlayerId;
      card: string;
      choices: Choice[];
      canSkip: boolean;
      picksLeft: number;
      picked: number[];
    };

export interface TradeOffer {
  cash: number;
  squares: number[];
  cards: HeldCard[];
}

export interface Trade {
  id: number;
  from: PlayerId;
  to: PlayerId;
  give: TradeOffer;
  get: TradeOffer;
}

export type Phase = 'roll' | 'resolving' | 'endTurn' | 'finished';

export type WinReason = 'time' | 'triple' | 'line' | 'resorts' | 'lastStanding';

export interface GameConfig {
  mode: Mode;
  players: { id: PlayerId; name: string }[];
  seed: number;
  /** Só para testes: permite forçar dados com ROLL { dice }. */
  allowForcedDice?: boolean;
  /** Só para testes: sem festivais sorteados no início. */
  noFestivals?: boolean;
  /** Só para calibrar: dinheiro extra de cada posição na ordem (substitui a tabela SEAT_BONUS). */
  seatBonus?: number[];
}

export interface GameState {
  rulesVersion: string;
  mode: Mode;
  durationMs: number;
  players: Player[];
  /** Dono e nível de cada casa do tabuleiro (null = sem dono ou não comprável). */
  props: (Ownership | null)[];
  /** Cidades com festival (aluguel x2 a partida toda), sorteadas no início. */
  festivals: number[];
  /** Cidade-sede do Campeonato Mundial e o multiplicador atual (x2, x3...). */
  championship: { square: number; mult: number } | null;
  current: number;
  turnCounter: number;
  round: number;
  phase: Phase;
  doublesInRow: number;
  extraRoll: boolean;
  lastRoll: Roll | null;
  queue: Step[];
  prompt: Prompt | null;
  debts: Debt[];
  deck: string[];
  discard: string[];
  pendingCard: string | null;
  caosAdded: boolean;
  effects: Effect[];
  /** Rodada em que o jogador sofreu o último ataque destrutivo. */
  attackedRound: Record<PlayerId, number>;
  /** Nível de tensão (só visual e som; também libera as cartas de Caos no nível 3). */
  tension: 1 | 2 | 3 | 4;
  bankruptcyHappened: boolean;
  firstHotelDone: boolean;
  elapsedMs: number;
  /** Tempo decorrido no início de cada rodada (para prever a duração da próxima). */
  roundStarts: number[];
  finalRound: boolean;
  rng: number;
  trades: Trade[];
  nextTradeId: number;
  allowForcedDice: boolean;
  /** Rodada da última movimentação de cada cidade (compra, construção ou aluguel pago). */
  lastVisit?: Record<number, number>;
  /** Terrenos protegidos do MST pela Carta Fazendeiro. */
  farms?: number[];
  winners: PlayerId[];
  winReason: WinReason | null;
}

export type Action =
  | { type: 'ROLL'; player: PlayerId; dice?: number[] }
  | { type: 'PAY_JAIL'; player: PlayerId }
  | { type: 'PAY_TAX'; player: PlayerId }
  /** Lojinha: comprar uma carta na sua vez, antes de rolar. */
  | { type: 'BUY_CARD'; player: PlayerId; card: HeldCard }
  /** MST: pagar o fazendeiro para expulsar a invasão. */
  | { type: 'EXPEL_MST'; player: PlayerId }
  /** MST: deixar a invasão acontecer (perde um nível). */
  | { type: 'ACCEPT_MST'; player: PlayerId }
  /** Tentar sonegar (dice só nos testes). */
  | { type: 'EVADE_TAX'; player: PlayerId; dice?: number[] }
  | { type: 'USE_JAIL_CARD'; player: PlayerId }
  /** Responde à decisão de compra/construção: até qual nível construir. */
  | { type: 'BUILD'; player: PlayerId; level: number }
  | { type: 'SKIP_BUILD'; player: PlayerId }
  | { type: 'TAKEOVER'; player: PlayerId }
  | { type: 'SKIP_TAKEOVER'; player: PlayerId }
  | { type: 'HOST'; player: PlayerId; square: number }
  | { type: 'TOUR'; player: PlayerId; to: number }
  | { type: 'SKIP_TOUR'; player: PlayerId }
  | { type: 'CHOOSE'; player: PlayerId; choice: string }
  /** Vende uma propriedade ao banco (para pagar dívida). */
  | { type: 'SELL'; player: PlayerId; square: number }
  | { type: 'PAY_DEBT'; player: PlayerId }
  | { type: 'DECLARE_BANKRUPTCY'; player: PlayerId }
  | { type: 'AUTO_SETTLE_DEBT'; player: PlayerId }
  | { type: 'PROPOSE_TRADE'; player: PlayerId; to: PlayerId; give: TradeOffer; get: TradeOffer }
  | { type: 'ACCEPT_TRADE'; player: PlayerId; tradeId: number }
  | { type: 'REJECT_TRADE'; player: PlayerId; tradeId: number }
  | { type: 'CANCEL_TRADE'; player: PlayerId; tradeId: number }
  | { type: 'END_TURN'; player: PlayerId }
  | { type: 'TICK' };

export type GameEvent =
  | { type: 'gameStarted'; mode: Mode; order: PlayerId[]; festivals: number[] }
  | { type: 'diceRolled'; player: PlayerId; roll: Roll }
  | { type: 'moved'; player: PlayerId; from: number; to: number; how: 'walk' | 'teleport' | 'back' | 'magnet' }
  | { type: 'passedStart'; player: PlayerId; amount: number }
  /** Comprou uma carta na lojinha. */
  | { type: 'cardBought'; player: PlayerId; card: HeldCard; price: number }
  /** Contador: livrou do Faz o L. */
  | { type: 'taxExempt'; player: PlayerId; amount: number }
  /** O MST chegou numa cidade parada. */
  | { type: 'mstArrived'; owner: PlayerId; square: number }
  /** O fazendeiro mandou o MST embora (paid = pagou na hora; senão era terreno protegido). */
  | { type: 'mstExpelled'; owner: PlayerId; square: number; paid: number }
  /** A invasão derrubou um nível da cidade. */
  | { type: 'mstInvaded'; owner: PlayerId; square: number; from: number; to: number }
  /** Carta Fazendeiro: terreno protegido do MST para sempre. */
  | { type: 'farmProtected'; player: PlayerId; square: number }
  /** Sonegação: os dados decidiram. */
  | { type: 'taxEvasion'; player: PlayerId; dice: number[]; total: number; success: boolean; amount: number }
  /** Ímã do Azarão: o dado especial (50%) decidiu se o peão é puxado para a cidade do azarão. */
  | { type: 'magnetRolled'; player: PlayerId; from: number; square: number; owner: PlayerId; pulled: boolean }
  | { type: 'sentToJail'; player: PlayerId; reason: string }
  | { type: 'leftJail'; player: PlayerId; how: 'paid' | 'card' | 'doubles' | 'forced' }
  | { type: 'jailRollFailed'; player: PlayerId; attempts: number }
  /** Comprou o terreno/resort e/ou construiu: `from` → `level`. */
  | { type: 'built'; player: PlayerId; square: number; from: number; level: Level; cost: number }
  | { type: 'hotelBuilt'; player: PlayerId; square: number; firstOfGame: boolean }
  | { type: 'takeover'; buyer: PlayerId; from: PlayerId; square: number; price: number }
  | {
      type: 'rentPaid';
      payer: PlayerId;
      owner: PlayerId;
      square: number;
      amount: number;
      facada: boolean;
      mortal: boolean;
    }
  | { type: 'rentWaived'; payer: PlayerId; square: number; reason: string }
  | { type: 'paid'; from: PlayerId; to: Creditor; amount: number; reason: string }
  | { type: 'received'; player: PlayerId; amount: number; reason: string }
  | { type: 'debtOpened'; debtor: PlayerId; creditor: Creditor; amount: number; reason: string }
  | { type: 'propertySold'; player: PlayerId; square: number; amount: number }
  | { type: 'bankrupt'; player: PlayerId; creditor: Creditor; paid: number; forgiven: number }
  | { type: 'groupCompleted'; player: PlayerId; group: number }
  | { type: 'championship'; player: PlayerId; square: number; mult: number }
  | { type: 'worldTourReady'; player: PlayerId }
  | { type: 'traveled'; player: PlayerId; from: number; to: number; cost: number }
  /** clover: o trevo da Sorte de Malandro trocou a carta de cima por uma melhor. */
  | { type: 'cardDrawn'; player: PlayerId; card: string; clover?: boolean }
  | { type: 'cardKept'; player: PlayerId; card: HeldCard }
  | { type: 'cardDiscardedHandFull'; player: PlayerId; card: string }
  | { type: 'cardUsed'; player: PlayerId; card: HeldCard }
  | { type: 'cardNoEffect'; player: PlayerId; card: string }
  | { type: 'cardLost'; player: PlayerId; card: HeldCard }
  | { type: 'cardStolen'; from: PlayerId; to: PlayerId; card: HeldCard }
  | { type: 'houseDestroyed'; owner: PlayerId; square: number; by: PlayerId | null; card: string }
  | { type: 'shieldBlocked'; target: PlayerId; attacker: PlayerId; card: string }
  | { type: 'interdicted'; square: number; untilTurn: number }
  | { type: 'swapped'; a: PlayerId; b: PlayerId }
  | { type: 'forcedPurchase'; buyer: PlayerId; from: PlayerId; square: number; price: number }
  | { type: 'effectStarted'; effect: Effect }
  | { type: 'tradeProposed'; trade: Trade }
  | { type: 'tradeAccepted'; trade: Trade }
  | { type: 'tradeRejected'; tradeId: number; by: PlayerId }
  | { type: 'tradeCancelled'; tradeId: number }
  | { type: 'tensionChanged'; level: 1 | 2 | 3 | 4 }
  | { type: 'caosAdded' }
  | { type: 'finalRoundStarted' }
  | { type: 'turnStarted'; player: PlayerId; turn: number; round: number }
  | {
      type: 'gameEnded';
      winners: PlayerId[];
      reason: WinReason;
      ranking: { player: PlayerId; worth: number }[];
    };

export interface ApplyResult {
  state: GameState;
  events: GameEvent[];
}

export class EngineError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'EngineError';
  }
}
